import React, { useState } from 'react';
import { Network, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { AnalyticsData } from '../../types/analytics';
import { anyTopicMatchesFilter } from '../../utils/topic-filter.util';

interface TopicRelationshipsCardProps {
  analytics?: AnalyticsData;
  selectedTopics?: string[];
  filterTopics?: string[];
  onTopicClick?: (topic: string) => void;
}

export const TopicRelationshipsCard: React.FC<TopicRelationshipsCardProps> = ({
  analytics,
  selectedTopics = [],
  filterTopics,
  onTopicClick,
}) => {
  const [showAll, setShowAll] = useState(false);
  const cooccurrences = analytics?.topics?.cooccurrence || [];
  const activeFilter = filterTopics ?? selectedTopics;

  // Filter by selected topics if any are selected
  const filteredCooccurrences = activeFilter.length > 0
    ? cooccurrences.filter((co) =>
        anyTopicMatchesFilter([co.topic1, co.topic2], activeFilter),
      )
    : cooccurrences;

  const displayedCooccurrences = showAll ? filteredCooccurrences : filteredCooccurrences.slice(0, 10);

  if (filteredCooccurrences.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center gap-2 mb-4">
          <Network className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Topic Relationships</h3>
        </div>
        <p className="text-gray-500 text-sm">
          {selectedTopics.length > 0 
            ? 'No topic relationships found for selected topics' 
            : 'No topic relationships found'}
        </p>
      </div>
    );
  }

  // Get relationship color
  const getRelationshipColor = (relationship: string) => {
    switch (relationship) {
      case 'strong': return 'bg-green-100 text-green-800 border-green-200';
      case 'moderate': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'weak': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRelationshipIcon = (relationship: string) => {
    switch (relationship) {
      case 'strong': return '●●●';
      case 'moderate': return '●●○';
      case 'weak': return '●○○';
      default: return '○○○';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center gap-2 mb-4">
        <Network className="w-5 h-5 text-purple-600" />
        <h3 className="text-lg font-semibold text-gray-900">Topic Relationships</h3>
      </div>
      
      <p className="text-sm text-gray-600 mb-4">
        Topics that frequently appear together in responses
        {selectedTopics.length > 0 && (
          <span className="ml-2 text-blue-600 font-medium">
            (filtered to {selectedTopics.length} {selectedTopics.length === 1 ? 'topic' : 'topics'})
          </span>
        )}
      </p>

      <div className="space-y-3">
        {displayedCooccurrences.map((co, index) => (
          <div 
            key={index}
            className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-purple-300 transition-colors"
          >
            <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 text-xs font-bold">
              {index + 1}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:underline"
                  onClick={() => onTopicClick?.(co.topic1)}
                  title={`Filter by ${co.topic1}`}
                >
                  {co.topic1}
                </span>
                <span className="text-gray-400">↔</span>
                <span
                  className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:underline"
                  onClick={() => onTopicClick?.(co.topic2)}
                  title={`Filter by ${co.topic2}`}
                >
                  {co.topic2}
                </span>
              </div>
              
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getRelationshipColor(co.relationship)}`}>
                  <span className="mr-1">{getRelationshipIcon(co.relationship)}</span>
                  {co.relationship}
                </span>
                
                <span className="text-xs text-gray-600">
                  {co.frequency} {co.frequency === 1 ? 'response' : 'responses'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredCooccurrences.length > 10 && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowAll(!showAll)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
          >
            {showAll ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Show Less
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Show All {filteredCooccurrences.length} Relationships
              </>
            )}
          </button>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-start gap-2 text-xs text-gray-600">
          <TrendingUp className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>
            Strong relationships indicate topics frequently discussed together, 
            suggesting thematic connections in responses.
          </p>
        </div>
      </div>
    </div>
  );
};
